import React, { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import Config from "../../Config";
import { dateSlotLine, normalizeOrder } from "../../../utils/orders";
import {
  formatEuro,
  GOOGLE_REVIEW_URL,
  pastOrderId,
  REVIEW_MAX,
  reviewSubKey,
  summaryItems,
  summaryTotals,
} from "../../../utils/pastOrders";
import { SUPPORT_URL } from "../../../utils/support";
import ConfirmDialog from "./ConfirmDialog";
import { Icon, Sprite } from "./icons";
import { authHeaders, openReceipt, postReview, reorder } from "./pastApi";
import { reorderFailed, useReorder } from "./PastOrderCard";
import "./orders.css";

/**
 * Détail d'une commande passée (« Mes commandes - Passees et detail »,
 * D01 à D09), règles de app/(profile)/order-detail.tsx du mobile :
 *  - la commande est cherchée dans past_orders ; si elle est encore en
 *    cours, retour à « Mes commandes » ;
 *  - une lecture ratée n'est pas une commande introuvable : on propose de
 *    réessayer.
 */

const langOf = (code) => (code === "fn" ? "fr" : code === "du" ? "nl" : "en");

export function OrderDetail({ match, history, authUser, dispatch }) {
  const { t, i18n } = useTranslation("app");
  const lang = langOf(i18n.language);
  const id = String(match?.params?.id ?? "");

  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    setStatus("loading");
    return Axios({ url: `${Config.apiURL}/customer/orders`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => {
        const data = response?.data || {};
        const found = (data.past_orders || []).find((item) => String(item.id) === id);
        if (!found && (data.current_orders || []).some((item) => String(item.id) === id)) {
          history.replace("/orders");
          return;
        }
        setOrder(found ? normalizeOrder(found) : null);
        setStatus(found ? "ready" : "missing");
      })
      .catch(() => setStatus("error"));
  }, [authUser, history, id]);

  useEffect(() => {
    load();
  }, [load]);

  const [reorderState, reorderNow] = useReorder(
    () => dispatch(reorder(order, authUser)),
    () => history.push("/home"),
    () => setDialog(reorderFailed(t))
  );

  const back = () => history.push("/orders?tab=past");

  const downloadReceipt = async () => {
    if (receiptBusy) return;
    setReceiptBusy(true);
    try {
      await openReceipt(order.id, authUser);
    } catch (error) {
      setDialog({
        icon: "alert-circle",
        tone: "danger",
        title: t("orderDetail.receiptErrorTitle"),
        message: t("orderDetail.receiptErrorBody"),
        confirm: "OK",
      });
    } finally {
      setReceiptBusy(false);
    }
  };

  const reviewSaved = (review) => {
    setOrder((current) => ({ ...current, review }));
    if (review?.stars === 5) {
      // Même délai que le mobile, le temps que la carte se mette à jour.
      setTimeout(
        () =>
          setDialog({
            title: t("orderDetail.fiveStarTitle"),
            message: t("orderDetail.fiveStarMsg"),
            cancel: t("orderDetail.maybeLater"),
            confirm: t("orderDetail.googleReview"),
            onConfirm: () => window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer"),
          }),
        400
      );
    }
  };

  const closeDialog = () => setDialog(null);

  return (
    <div className="wosh-ord">
      <Sprite />
      <div className="wosh-ord__page wosh-od">
        <header className="wosh-od__head">
          <button type="button" className="wosh-od__back" onClick={back} aria-label="←">
            <Icon name="arrow-back" />
          </button>
          <div className="wosh-ord__titles">
            {order && (
              <div className="wosh-ord__eyebrow">{t("orderDetail.eyebrow", { id: pastOrderId(order) })}</div>
            )}
            <h1 className="wosh-od__title">{t("orderDetail.title")}</h1>
          </div>
        </header>

        {status === "loading" ? (
          <div className="wosh-od__state" aria-busy="true">
            <span className="wosh-ord__spinner" />
          </div>
        ) : status === "error" ? (
          <div className="wosh-od__state">
            <div className="wosh-od__state-icon wosh-od__state-icon--muted">
              <Icon name="cloud-offline-outline" />
            </div>
            <div className="wosh-od__state-title">{t("orderDetail.loadErrorTitle")}</div>
            <div className="wosh-od__state-sub">{t("orderDetail.loadErrorSub")}</div>
            <button type="button" className="wosh-od__retry" onClick={load}>
              {t("orderDetail.loadErrorRetry")}
            </button>
          </div>
        ) : status === "missing" ? (
          <div className="wosh-od__state">
            <div className="wosh-od__state-icon">
              <Icon name="receipt-outline" />
            </div>
            <div className="wosh-od__state-title">{t("orderDetail.orderNotFound")}</div>
            <div className="wosh-od__state-sub">{t("orderDetail.orderNotFoundSub")}</div>
          </div>
        ) : (
          <div className="wosh-od__cols">
            <div className="wosh-od__col">
              <RatingCard key={order.id} order={order} t={t} authUser={authUser} onSaved={reviewSaved} />

              <button
                type="button"
                className={`wosh-od__again wosh-od__again--${reorderState}`}
                disabled={reorderState === "loading"}
                onClick={reorderNow}
              >
                {reorderState === "loading" ? (
                  <span className="wosh-po__spin wosh-po__spin--light" />
                ) : (
                  <Icon name="refresh" />
                )}
                {reorderState === "loading" ? t("orderDetail.addingToCart") : t("orderDetail.orderAgain")}
              </button>

              <div className="wosh-od__card wosh-od__actions">
                <button type="button" className="wosh-od__action" onClick={downloadReceipt} disabled={receiptBusy}>
                  <span className="wosh-od__tile">
                    {receiptBusy ? <span className="wosh-po__spin" /> : <Icon name="receipt-outline" />}
                  </span>
                  <span className="wosh-od__action-text">
                    <span className="wosh-od__action-title">{t("orderDetail.downloadReceipt")}</span>
                    <span className="wosh-od__action-sub">{t("orderDetail.receiptSub")}</span>
                  </span>
                  <Icon name="chevron-forward" className="wosh-od__chevron" />
                </button>
                <div className="wosh-od__hairline" />
                <a className="wosh-od__action" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                  <span className="wosh-od__tile">
                    <Icon name="help-circle-outline" />
                  </span>
                  <span className="wosh-od__action-text">
                    <span className="wosh-od__action-title">{t("orderDetail.getHelp")}</span>
                    <span className="wosh-od__action-sub">{t("orderDetail.getHelpSub")}</span>
                  </span>
                  <Icon name="chevron-forward" className="wosh-od__chevron" />
                </a>
              </div>
            </div>

            <div className="wosh-od__col wosh-od__col--side">
              <ScheduleCard order={order} t={t} lang={lang} />
              <SummaryCard order={order} t={t} lang={lang} />
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(dialog)}
        icon={dialog?.icon}
        tone={dialog?.tone}
        title={dialog?.title}
        message={dialog?.message}
        cancel={dialog?.cancel}
        confirm={dialog?.confirm}
        onCancel={closeDialog}
        onConfirm={() => {
          dialog?.onConfirm?.();
          closeDialog();
        }}
      />
    </div>
  );
}

/**
 * Livré + étoiles + avis écrit (RatingCard du mobile). Une fois l'avis
 * envoyé, les étoiles sont figées jusqu'à « Modifier ».
 */
export function RatingCard({ order, t, authUser, onSaved }) {
  const existing = order.review ?? null;
  const [rating, setRating] = useState(existing?.stars ?? 0);
  const [hover, setHover] = useState(0);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(existing?.review_content ?? "");
  const [submitted, setSubmitted] = useState(Boolean(existing));
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => (mounted.current = false), []);

  const readOnly = submitted && !editing;
  const shown = readOnly ? rating : hover || rating;
  const labels = t("orderDetail.ratingLabels", { returnObjects: true });

  const rate = (n) => {
    if (readOnly) return;
    setRating(n);
    setHover(0);
    if (!open) setOpen(true);
  };

  const edit = () => {
    setEditing(true);
    setSubmitted(false);
    setOpen(true);
  };

  const submit = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const review = await postReview({ stars: rating, reviewContent: text, orderId: order.id }, authUser);
      if (!mounted.current) return;
      setSubmitted(true);
      setEditing(false);
      setOpen(false);
      onSaved(review ?? { stars: rating, review_content: text });
    } catch (err) {
      if (!mounted.current) return;
      const server = err?.response?.data?.errors?.[0] || err?.response?.data?.message;
      setError(typeof server === "string" ? server : t("orderDetail.couldNotSaveReview"));
    } finally {
      if (mounted.current) setPending(false);
    }
  };

  return (
    <div className="wosh-od__card wosh-od__rating">
      <div className="wosh-od__rating-head">
        <span className="wosh-od__done">
          <Icon name="checkmark-sharp" />
        </span>
        <div className="wosh-od__rating-text">
          <div className="wosh-od__rating-title">{t("orderDetail.delivered")}</div>
          <div className="wosh-od__rating-sub">{t(reviewSubKey({ pending, submitted, editing, rating }))}</div>
        </div>
        {pending ? (
          <span className="wosh-po__spin" />
        ) : (
          readOnly && (
            <button type="button" className="wosh-od__edit" onClick={edit}>
              <Icon name="pencil" />
              {t("orderDetail.editReviewBtn")}
            </button>
          )
        )}
      </div>

      <div className={`wosh-od__stars-wrap${readOnly ? " wosh-od__stars-wrap--read" : ""}`}>
        <div className="wosh-od__stars" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              type="button"
              key={n}
              className={`wosh-od__star${n <= shown ? " wosh-od__star--on" : ""}`}
              aria-label={`${n}/5`}
              aria-pressed={n <= rating}
              disabled={readOnly}
              onMouseEnter={() => !readOnly && setHover(n)}
              onClick={() => rate(n)}
            >
              <Icon name="star" />
            </button>
          ))}
        </div>
        {shown > 0 && Array.isArray(labels) && <div className="wosh-od__star-label">{labels[shown]}</div>}
      </div>

      {error && <div className="wosh-od__error">{error}</div>}

      {open && !readOnly && (
        <div className="wosh-od__review">
          <textarea
            className="wosh-od__textarea"
            rows={3}
            maxLength={REVIEW_MAX}
            placeholder={t("orderDetail.tellUsMore")}
            value={text}
            disabled={pending}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="wosh-od__review-foot">
            <span className="wosh-od__counter">
              {text.length}/{REVIEW_MAX}
            </span>
            <button type="button" className="wosh-od__submit" onClick={submit} disabled={pending || rating === 0}>
              {t("orderDetail.submitReview")}
            </button>
          </div>
        </div>
      )}

      {submitted && text.trim().length > 0 && <div className="wosh-od__quote">« {text} »</div>}

      {rating > 0 && !submitted && !open && (
        <button type="button" className="wosh-od__add" onClick={() => setOpen(true)}>
          {t("orderDetail.addWrittenReview")}
        </button>
      )}
    </div>
  );
}

function ScheduleCard({ order, t, lang }) {
  return (
    <div className="wosh-od__card">
      <div className="wosh-od__card-title">{t("orderDetail.scheduleTitle")}</div>
      <div className="wosh-od__sched">
        <span className="wosh-od__tile">
          <Icon name="location-outline" />
        </span>
        <span className="wosh-od__sched-text">
          <span className="wosh-od__sched-label">{t("orderDetail.pickupLabel")}</span>
          <span className="wosh-od__sched-value">{dateSlotLine(order.pickup_date, order.pickup_slot, lang) || "—"}</span>
        </span>
      </div>
      <div className="wosh-od__hairline" />
      <div className="wosh-od__sched">
        <span className="wosh-od__tile">
          <Icon name="car-outline" />
        </span>
        <span className="wosh-od__sched-text">
          <span className="wosh-od__sched-label">{t("orderDetail.deliveryLabel")}</span>
          <span className="wosh-od__sched-value">
            {dateSlotLine(order.delivery_date, order.delivery_slot, lang) || "—"}
          </span>
        </span>
      </div>
    </div>
  );
}

function SummaryCard({ order, t, lang }) {
  const items = summaryItems(order, lang, Config.apiURL);
  const { subtotal, total } = summaryTotals(order);

  return (
    <div className="wosh-od__card">
      <div className="wosh-od__card-title">{t("orderDetail.summaryTitle")}</div>
      {items.length ? (
        items.map((item, i) => (
          <div className="wosh-od__item" key={i}>
            <span className="wosh-od__tile wosh-od__tile--initial">
              {item.icon ? <img src={item.icon} alt="" /> : item.initial}
            </span>
            <span className="wosh-od__item-text">
              <span className="wosh-od__item-name">{item.label}</span>
              <span className="wosh-od__item-meta">{item.meta}</span>
            </span>
            {item.price && <span className="wosh-od__item-price">{item.price}</span>}
          </div>
        ))
      ) : (
        <div className="wosh-od__item-meta">{t("orderDetail.noItemDetails")}</div>
      )}
      <div className="wosh-od__hairline" />
      <div className="wosh-od__line">
        <span>{t("orderDetail.subtotal")}</span>
        <strong>{formatEuro(subtotal)}</strong>
      </div>
      <div className="wosh-od__line">
        <span>{t("orderDetail.delivery")}</span>
        <strong className="wosh-od__free">{t("orderDetail.free")}</strong>
      </div>
      <div className="wosh-od__hairline" />
      <div className="wosh-od__line wosh-od__line--total">
        <span>{t("orderDetail.totalPaid")}</span>
        <span>{formatEuro(total)}</span>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(OrderDetail);
